import { Suspense } from "react";
import type { RouteObject } from "src/router";
import { Navigate, useParams } from "src/router";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { AdStudioDemoRoute } from "src/pages/ad-studio-demo/ad-studio-demo-route";
import {
  useAdStudioDemoFlagWhenReady,
  useIsAdStudioDemoEnabled,
} from "src/pages/ad-studio-demo/use-is-ad-studio-demo-enabled";
import { lazyNamed } from "src/utils/lazy-named";
import {
  CHANNEL_SIZE_SEGMENT,
  SIZE_VARIANT_SEGMENT,
  VERSION_SEGMENT,
} from "@hightouch/lib/llm/asset-comments/paths";
import { AdStudioLandingSkeleton } from "./projects/components/landing-skeleton";
import { OpportunitiesFeatureRoute } from "./components/opportunities-feature-route";

const FeatureFlagAdStudioRoutes = lazyNamed(
  () => import("./components/feature-flag-routes"),
  "FeatureFlagAdStudioRoutes",
);
const AdsSectionLayout = lazyNamed(
  () => import("./projects/ads-section-layout"),
  "AdsSectionLayout",
);
const AllAdsView = lazyNamed(() => import("./projects/all-ads"), "AllAdsView");
const ChatsGridView = lazyNamed(
  () => import("./projects/chats-grid"),
  "ChatsGridView",
);
const AdStudioProjects = lazyNamed(
  () => import("./projects"),
  "AdStudioProjects",
);
const FolderDetailPage = lazyNamed(
  () => import("./folders/detail"),
  "FolderDetailPage",
);
const FolderDetailLaunchTab = lazyNamed(
  () => import("./folders/detail/launch-tab"),
  "FolderDetailLaunchTab",
);
const IdeasFeedPage = lazyNamed(() => import("./ideas"), "IdeasFeedPage");
const CreateProjectPage = lazyNamed(
  () => import("./projects/new"),
  "CreateProjectPage",
);
const ProjectDetailPage = lazyNamed(
  () => import("./projects/detail"),
  "ProjectDetailPage",
);
const ProjectDetailLaunchTab = lazyNamed(
  () => import("./projects/detail/launch-tab"),
  "ProjectDetailLaunchTab",
);
const LaunchPage = lazyNamed(
  () => import("./launch/launch-page"),
  "LaunchPage",
);
const AdArtifactsPage = lazyNamed(
  () => import("./ad-artifacts"),
  "AdArtifactsPage",
);
const CatalogAdsListPage = lazyNamed(
  () => import("./enriched-catalog"),
  "CatalogAdsListPage",
);
const FeedDetailPage = lazyNamed(
  () => import("./enriched-catalog/feed-detail"),
  "FeedDetailPage",
);
const TreatmentDetailPage = lazyNamed(
  () => import("./enriched-catalog/treatment-detail"),
  "TreatmentDetailPage",
);
const TreatmentTemplateEditorPage = lazyNamed(
  () => import("./enriched-catalog/template-editor"),
  "TreatmentTemplateEditorPage",
);
const TemplatesPage = lazyNamed(() => import("./templates"), "TemplatesPage");
const TemplateDetailPage = lazyNamed(
  () => import("./templates/detail"),
  "TemplateDetailPage",
);
const TemplateEditorPage = lazyNamed(
  () => import("./templates/editor"),
  "TemplateEditorPage",
);
const AssetsPage = lazyNamed(() => import("./assets"), "AssetsPage");
const AssetsBrowse = lazyNamed(() => import("./assets"), "AssetsBrowse");
const LayersPage = lazyNamed(() => import("./layers"), "LayersPage");
const HtmlAdEditorPage = lazyNamed(
  () => import("./html-ad-editor"),
  "HtmlAdEditorPage",
);
const InsightsComponentPlayground = lazyNamed(
  () => import("./insights/component-playground"),
  "InsightsComponentPlayground",
);
const CompetitorDetailPage = lazyNamed(
  () => import("./insights/competitor-detail-page"),
  "CompetitorDetailPage",
);
const InsightsPage = lazyNamed(
  () => import("./insights/insights-page"),
  "InsightsPage",
);
const InsightsSectionLayout = lazyNamed(
  () => import("src/pages/ad-studio-demo/insights/insights-section-layout"),
  "InsightsSectionLayout",
);
const InsightsHome = lazyNamed(
  () => import("src/pages/ad-studio-demo/insights/home/insights-home"),
  "InsightsHome",
);
const PerformanceTab = lazyNamed(
  () => import("./insights/performance-tab"),
  "PerformanceTab",
);
const FatigueTab = lazyNamed(
  () => import("./insights/fatigue-tab"),
  "FatigueTab",
);
const CompetitorsTab = lazyNamed(
  () => import("./insights/competitors-tab"),
  "CompetitorsTab",
);
const SignalsTab = lazyNamed(
  () => import("./insights/signals/signals-tab"),
  "SignalsTab",
);
const OpportunitiesFeedPage = lazyNamed(
  () => import("./insights/opportunities"),
  "OpportunitiesFeedPage",
);
const OpportunityDetailPage = lazyNamed(
  () => import("./insights/opportunities/detail-page"),
  "OpportunityDetailPage",
);
const ConceptDetailModal = lazyNamed(
  () => import("./briefs/concept-detail-modal"),
  "ConceptDetailModal",
);
// Insights ships two shells: the production tabbed `DetailPage` and the demo
// left-nav layout. Only the branch that renders is fetched, so flag-off users
// never download the demo chunk.
const InsightsLayout = () =>
  useIsAdStudioDemoEnabled() ? <InsightsSectionLayout /> : <InsightsPage />;

// `home` only exists in the demo shell; the production shell opens on
// Performance. Unlike the layout swap above, this one can't read the flag
// optimistically: a redirect unmounts this component, so a pre-initialization
// `false` would strand flag-on users on Performance for the rest of the
// session. Hold the frame until LaunchDarkly resolves.
const InsightsIndexRedirect = () => {
  const isDemoEnabled = useAdStudioDemoFlagWhenReady();

  if (isDemoEnabled === undefined) return null;

  return <Navigate to={isDemoEnabled ? "home" : "performance"} replace />;
};

// Styles moved to Context Hub; redirect old links (e.g. copied style URLs).
const StyleDetailRedirect = () => {
  const { styleId } = useParams<{ styleId: string }>();
  return <Navigate to={`/context-hub/styles/${styleId}`} replace />;
};

// The concept/ad detail view, rendered as a full-screen modal over whichever
// list mounted it (project, folder, or a synthetic grid). Nesting these as
// children keeps the ancestor route element (the list + shell) mounted behind
// the modal, so closing returns to the exact origin view.
// Local Suspense with a null fallback: these lazy modal chunks would otherwise
// suspend up to the page-level PageSuspense boundary, replacing the whole grid
// behind the modal with a spinner for the duration of the chunk load. With a
// null fallback the grid stays visible and the modal appears (with its own
// skeleton) once the chunk lands.
const modalSuspense = (element: JSX.Element) => (
  <Suspense fallback={null}>{element}</Suspense>
);

const conceptModalChildRoutes: RouteObject[] = [
  // Old motion-editor deep links now open the same unified editor. Keeping
  // these routes makes bookmarks and links from existing sessions durable.
  {
    path: "concepts/:adBriefId/:assetId/motion/*",
    spacesAccess: "hidden-from-limited-view" as const,
    element: modalSuspense(<ConceptDetailModal />),
  },
  {
    path: "concepts/:adBriefId/:assetId/layers/:layerId",
    spacesAccess: "hidden-from-limited-view" as const,
    element: modalSuspense(<ConceptDetailModal />),
  },
  {
    path: `concepts/:adBriefId/:assetId/${VERSION_SEGMENT}/:versionId`,
    spacesAccess: "hidden-from-limited-view" as const,
    element: modalSuspense(<ConceptDetailModal />),
  },
  {
    path: `concepts/:adBriefId/:assetId/${CHANNEL_SIZE_SEGMENT}/:channelSizeId`,
    spacesAccess: "hidden-from-limited-view" as const,
    element: modalSuspense(<ConceptDetailModal />),
  },
  {
    path: `concepts/:adBriefId/:assetId/${SIZE_VARIANT_SEGMENT}/:sizeVariantId`,
    spacesAccess: "hidden-from-limited-view" as const,
    element: modalSuspense(<ConceptDetailModal />),
  },
  {
    path: "concepts/:adBriefId/:assetId",
    spacesAccess: "hidden-from-limited-view" as const,
    element: modalSuspense(<ConceptDetailModal />),
  },
  {
    path: "concepts/:adBriefId",
    spacesAccess: "hidden-from-limited-view" as const,
    element: modalSuspense(<ConceptDetailModal />),
  },
];

// The product catalog also moved to Context Hub; redirect old links (e.g.
// catalog links embedded in previously generated campaign briefs).
const CatalogItemRedirect = () => {
  const { catalogItemId } = useParams<{ catalogItemId: string }>();
  return <Navigate to={`/context-hub/catalog/${catalogItemId}`} replace />;
};

export const routes: RouteObject[] = [
  {
    path: "ad-studio",
    spacesAccess: "hidden-from-limited-view",
    element: (
      <PermissionedRoute
        permission={{
          v2: { resource: "workspace", grant: "can_view_agents" },
        }}
        redirect="/"
      >
        <FeatureFlagAdStudioRoutes />
      </PermissionedRoute>
    ),
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="projects" replace />,
      },
      {
        path: "projects/new",
        spacesAccess: "hidden-from-limited-view",
        element: <CreateProjectPage />,
      },
      {
        spacesAccess: "hidden-from-limited-view",
        element: <AdsSectionLayout />,
        children: [
          {
            path: "ads",
            spacesAccess: "hidden-from-limited-view",
            element: <AllAdsView />,
            children: conceptModalChildRoutes,
          },
          {
            // Synthetic "My favorites" root: starred ads across all projects.
            path: "favorites",
            spacesAccess: "hidden-from-limited-view",
            element: <AllAdsView view="favorites" />,
            children: conceptModalChildRoutes,
          },
          {
            // Synthetic "Created by you" root: ads from projects you created.
            path: "created-by-you",
            spacesAccess: "hidden-from-limited-view",
            element: <AllAdsView view="created-by-you" />,
            children: conceptModalChildRoutes,
          },
          {
            // Grid of chat (project) cards for the whole workspace, reached
            // via the "All chats" section's "View all" arrow in the sidebar.
            path: "chats",
            spacesAccess: "hidden-from-limited-view",
            element: <ChatsGridView />,
          },
          {
            // Same grid scoped to the current user's chats ("My chats").
            path: "chats/mine",
            spacesAccess: "hidden-from-limited-view",
            element: <ChatsGridView view="mine" />,
          },
          {
            // Default landing: the create-a-project surface (chat + quick
            // prompts). Project navigation lives in the sidebar. A route-level
            // Suspense gives it a layout-matching skeleton (heading + chat input
            // + quick-prompt tiles) instead of the shell's generic spinner,
            // covering both the lazy chunk load and the page's suspense query.
            path: "projects",
            spacesAccess: "hidden-from-limited-view",
            element: (
              <Suspense fallback={<AdStudioLandingSkeleton />}>
                <AdStudioProjects />
              </Suspense>
            ),
          },
          {
            // Project detail embedded in the sidebar shell (sidebar persists).
            // crumbs={[]} drops the "All chats" breadcrumb — the sidebar is
            // the project navigation now.
            path: "projects/:projectId",
            spacesAccess: "hidden-from-limited-view",
            element: <ProjectDetailPage crumbs={[]} />,
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view" as const,
              },
              {
                path: "launch",
                spacesAccess: "hidden-from-limited-view" as const,
                element: <ProjectDetailLaunchTab />,
              },
              ...conceptModalChildRoutes,
            ],
          },
          {
            // Folder detail embedded in the sidebar shell, with an Ads tab and
            // a Launch tab for launching the folder's ads.
            path: "folders/:folderId",
            spacesAccess: "hidden-from-limited-view",
            element: <FolderDetailPage />,
            children: [
              {
                index: true,
                spacesAccess: "hidden-from-limited-view" as const,
              },
              {
                path: "launch",
                spacesAccess: "hidden-from-limited-view" as const,
                element: <FolderDetailLaunchTab />,
              },
              ...conceptModalChildRoutes,
            ],
          },
          {
            // Ideas feed embedded in the sidebar shell (sidebar persists), so
            // the "Ideas" synthetic root renders its UI beside the nav.
            path: "ideas",
            spacesAccess: "hidden-from-limited-view",
            element: <IdeasFeedPage />,
            children: [
              {
                path: ":projectId",
                spacesAccess: "hidden-from-limited-view" as const,
              },
            ],
          },
          {
            // Workspace-wide launched ads embedded in the sidebar shell, backing
            // the "Launched" synthetic root.
            path: "launch",
            spacesAccess: "hidden-from-limited-view",
            element: <LaunchPage />,
          },
        ],
      },
      {
        path: "templates",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute flagKey="adStudioTemplates" redirect="/ad-studio">
            <TemplatesPage />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "templates/:templateId",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute flagKey="adStudioTemplates" redirect="/ad-studio">
            <TemplateDetailPage />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "templates/:templateId/edit",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute flagKey="adStudioTemplates" redirect="/ad-studio">
            <TemplateEditorPage />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "styles",
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="/context-hub/styles" replace />,
      },
      {
        path: "styles/:styleId",
        spacesAccess: "hidden-from-limited-view",
        element: <StyleDetailRedirect />,
      },
      {
        path: "catalog-ads",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="adStudioEnrichedCatalogs"
            redirect="/ad-studio"
          >
            <CatalogAdsListPage />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "catalog-ads/:feedId",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="adStudioEnrichedCatalogs"
            redirect="/ad-studio"
          >
            <FeedDetailPage />
          </FeatureFlagRoute>
        ),
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view" as const,
          },
          {
            path: "output",
            spacesAccess: "hidden-from-limited-view" as const,
          },
        ],
      },
      {
        path: "catalog-ads/:feedId/treatments/:treatmentId",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="adStudioEnrichedCatalogs"
            redirect="/ad-studio"
          >
            <TreatmentDetailPage />
          </FeatureFlagRoute>
        ),
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view" as const,
          },
          {
            path: "preview",
            spacesAccess: "hidden-from-limited-view" as const,
          },
        ],
      },
      {
        path: "catalog-ads/:feedId/treatments/:treatmentId/template",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <FeatureFlagRoute
            flagKey="adStudioEnrichedCatalogs"
            redirect="/ad-studio"
          >
            <TreatmentTemplateEditorPage />
          </FeatureFlagRoute>
        ),
      },
      {
        path: "artifacts",
        spacesAccess: "hidden-from-limited-view",
        element: <AdArtifactsPage />,
      },
      {
        path: "artifacts/:artifactId",
        spacesAccess: "hidden-from-limited-view",
        element: <AdArtifactsPage />,
      },
      {
        path: "catalog",
        spacesAccess: "hidden-from-limited-view",
        element: <Navigate to="/context-hub/catalog" replace />,
      },
      {
        path: "catalog/:catalogItemId",
        spacesAccess: "hidden-from-limited-view",
        element: <CatalogItemRedirect />,
      },
      {
        path: "assets",
        spacesAccess: "hidden-from-limited-view",
        element: <AssetsPage />,
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <AssetsBrowse />,
          },
          {
            path: "browse",
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="/ad-studio/assets" replace />,
          },
          {
            path: "settings",
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="/ad-studio/assets" replace />,
          },
        ],
      },
      {
        path: "layers",
        spacesAccess: "hidden-from-limited-view" as const,
        element: <LayersPage />,
      },
      {
        path: "html-ad-editor",
        spacesAccess: "hidden-from-limited-view" as const,
        element: <HtmlAdEditorPage />,
      },
      {
        path: "insights",
        spacesAccess: "hidden-from-limited-view",
        element: <InsightsLayout />,
        children: [
          {
            index: true,
            element: <InsightsIndexRedirect />,
            spacesAccess: "hidden-from-limited-view" as const,
          },
          {
            path: "home",
            element: (
              <AdStudioDemoRoute redirect="/ad-studio/insights/performance">
                <InsightsHome />
              </AdStudioDemoRoute>
            ),
            spacesAccess: "hidden-from-limited-view" as const,
          },
          {
            path: "performance",
            element: <PerformanceTab />,
            spacesAccess: "hidden-from-limited-view" as const,
            children: [
              {
                path: ":entityType/:entityId",
                spacesAccess: "hidden-from-limited-view" as const,
              },
            ],
          },
          {
            path: "fatigue",
            element: <FatigueTab />,
            spacesAccess: "hidden-from-limited-view" as const,
            children: [
              {
                path: ":entityType/:entityId",
                spacesAccess: "hidden-from-limited-view" as const,
              },
            ],
          },
          {
            path: "competitors",
            element: <CompetitorsTab />,
            spacesAccess: "hidden-from-limited-view" as const,
          },
          {
            path: "signals",
            element: <SignalsTab />,
            spacesAccess: "hidden-from-limited-view" as const,
            children: [
              {
                path: "cards/:cardId",
                spacesAccess: "hidden-from-limited-view" as const,
              },
            ],
          },
          {
            path: "opportunities",
            element: (
              <OpportunitiesFeatureRoute redirect="/ad-studio/insights/signals">
                <OpportunitiesFeedPage />
              </OpportunitiesFeatureRoute>
            ),
            spacesAccess: "hidden-from-limited-view" as const,
          },
        ],
      },
      {
        path: "insights/components",
        spacesAccess: "hidden-from-limited-view",
        element: <InsightsComponentPlayground />,
      },
      {
        path: "insights/competitors/:pageId",
        spacesAccess: "hidden-from-limited-view",
        element: <CompetitorDetailPage />,
      },
      {
        // Opportunity detail is a full page with its own shell, so it lives
        // outside the tabbed insights layout (same pattern as competitors).
        path: "insights/opportunities/:cardId",
        spacesAccess: "hidden-from-limited-view",
        element: (
          <OpportunitiesFeatureRoute redirect="/ad-studio/insights/signals">
            <OpportunityDetailPage />
          </OpportunitiesFeatureRoute>
        ),
      },
    ],
  },
];
